import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiMenu,
  FiX,
  FiArrowRight,
  FiCheckCircle,
  FiZap,
  FiShield,
  FiCalendar,
  FiActivity,
  FiTrendingUp,
  FiStar,
  FiLayers,
  FiCpu,
  FiMapPin,
  FiDollarSign,
  FiPackage,
  FiGlobe,
  FiMessageCircle,
  FiRefreshCw,
  FiFileText,
  FiSmartphone,
  FiUsers,
} from "react-icons/fi";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import Footer from "./Footer";
import appImage from "../../assets/Image/DeskApp.png";
import Logo from "../../assets/Image/Effitrack.png";
import Stripe from "../../assets/Image/Stripe.png";
import ApplePay from "../../assets/Image/ApplePay.png";
import Visa from "../../assets/Image/Visa.png";
import Mastercard from "../../assets/Image/mastercard.jpg";

export default function WelcomePage() {
  const { t } = useTranslation("welcome");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const features = useMemo(
    () => [
      {
        title: t("features.scheduling.title"),
        desc: t("features.scheduling.desc"),
        icon: <FiCalendar className="h-6 w-6" />,
        accent: "from-cyan-400/50 via-cyan-500/20 to-transparent",
        takeaways: t("features.scheduling.takeaways", { returnObjects: true }),
      },
      {
        title: t("features.automations.title"),
        desc: t("features.automations.desc"),
        icon: <FiZap className="h-6 w-6" />,
        accent: "from-amber-400/50 via-amber-500/20 to-transparent",
        takeaways: t("features.automations.takeaways", { returnObjects: true }),
      },
      {
        title: t("features.financial.title"),
        desc: t("features.financial.desc"),
        icon: <FiTrendingUp className="h-6 w-6" />,
        accent: "from-emerald-400/40 via-emerald-500/10 to-transparent",
        takeaways: t("features.financial.takeaways", { returnObjects: true }),
      },
      {
        title: t("features.controls.title"),
        desc: t("features.controls.desc"),
        icon: <FiShield className="h-6 w-6" />,
        accent: "from-indigo-400/50 via-indigo-500/20 to-transparent",
        takeaways: t("features.controls.takeaways", { returnObjects: true }),
      },
    ],
    [t]
  );

  const workflow = useMemo(
    () => [
      {
        title: t("workflow.steps.capture.title"),
        copy: t("workflow.steps.capture.copy"),
        icon: <FiLayers className="h-5 w-5" />,
      },
      {
        title: t("workflow.steps.estimate.title"),
        copy: t("workflow.steps.estimate.copy"),
        icon: <FiCheckCircle className="h-5 w-5" />,
      },
      {
        title: t("workflow.steps.schedule.title"),
        copy: t("workflow.steps.schedule.copy"),
        icon: <FiCalendar className="h-5 w-5" />,
      },
      {
        title: t("workflow.steps.run.title"),
        copy: t("workflow.steps.run.copy"),
        icon: <FiCpu className="h-5 w-5" />,
      },
      {
        title: t("workflow.steps.bill.title"),
        copy: t("workflow.steps.bill.copy"),
        icon: <FiTrendingUp className="h-5 w-5" />,
      },
      {
        title: t("workflow.steps.report.title"),
        copy: t("workflow.steps.report.copy"),
        icon: <FiActivity className="h-5 w-5" />,
      },
    ],
    [t]
  );

  const proofPoints = useMemo(
    () => [
      { value: "38%", label: t("proofPoints.fasterJobTurnaround"), icon: <FiZap className="h-5 w-5 text-amber-500" /> },
      { value: "2.5h", label: t("proofPoints.adminSaved"), icon: <FiCheckCircle className="h-5 w-5 text-emerald-600" /> },
      { value: "5 min", label: t("proofPoints.approvalToScheduled"), icon: <FiCalendar className="h-5 w-5 text-cyan-600" /> },
    ],
    [t]
  );

  const payments = [
    { name: "Stripe", src: Stripe },
    { name: "Apple Pay", src: ApplePay },
    { name: "Visa", src: Visa },
    { name: "Mastercard", src: Mastercard },
  ];

  const testimonials = useMemo(
    () => [
      {
        name: t("testimonials.sydney.name"),
        role: t("testimonials.sydney.role"),
        quote: t("testimonials.sydney.quote"),
      },
      {
        name: t("testimonials.marcus.name"),
        role: t("testimonials.marcus.role"),
        quote: t("testimonials.marcus.quote"),
      },
    ],
    [t]
  );

  const moreFeatureItems = useMemo(
    () => [
      { key: "gps", icon: <FiMapPin className="h-5 w-5" />, color: "text-cyan-600", bg: "bg-cyan-50" },
      { key: "ai", icon: <FiCpu className="h-5 w-5" />, color: "text-violet-600", bg: "bg-violet-50" },
      { key: "payroll", icon: <FiDollarSign className="h-5 w-5" />, color: "text-emerald-600", bg: "bg-emerald-50" },
      { key: "inventory", icon: <FiPackage className="h-5 w-5" />, color: "text-amber-600", bg: "bg-amber-50" },
      { key: "marketing", icon: <FiGlobe className="h-5 w-5" />, color: "text-pink-600", bg: "bg-pink-50" },
      { key: "messaging", icon: <FiMessageCircle className="h-5 w-5" />, color: "text-sky-600", bg: "bg-sky-50" },
      { key: "recurring", icon: <FiRefreshCw className="h-5 w-5" />, color: "text-indigo-600", bg: "bg-indigo-50" },
      { key: "documents", icon: <FiFileText className="h-5 w-5" />, color: "text-slate-600", bg: "bg-slate-100" },
    ],
    []
  );

  const mobileAppFeatures = useMemo(
    () => t("mobileApp.features", { returnObjects: true }),
    [t]
  );

  const mobileAppHighlights = useMemo(
    () => [
      { key: "employeePortal", icon: <FiUsers className="h-5 w-5 text-cyan-600" />, desc: t("mobileApp.employeePortalDesc") },
      { key: "adminDashboard", icon: <FiActivity className="h-5 w-5 text-emerald-600" />, desc: t("mobileApp.adminDashboardDesc") },
      { key: "liveUpdates", icon: <FiZap className="h-5 w-5 text-amber-500" />, desc: t("mobileApp.liveUpdatesDesc") },
    ],
    [t]
  );

  const handleScrollTo = useCallback((id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  useEffect(() => {
    if (!features.length) return undefined;
    const interval = setInterval(
      () => setActiveFeature((prev) => (prev + 1) % features.length),
      4500
    );
    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-slate-50 via-white to-sky-50 text-slate-900">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-slate-200/80 bg-white/80 backdrop-blur-xl shadow-sm"
            : "bg-white/60 backdrop-blur"
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="group flex items-center gap-3">
            <div className="relative h-11 w-11 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-300/90 via-emerald-300/4 to-transparent blur-2xl" />
              <img src={Logo} alt="EffiTrack" className="relative h-full w-full object-contain" />
            </div>
            <span className="display-font text-xl font-semibold tracking-tight text-slate-900 transition-opacity group-hover:opacity-90">
              EffiTrack
            </span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            <button
              type="button"
              onClick={() => handleScrollTo("platform")}
              className="text-sm font-semibold text-slate-700 transition hover:text-slate-900"
            >
              {t("nav.platform")}
            </button>
            <button
              type="button"
              onClick={() => handleScrollTo("workflow")}
              className="text-sm font-semibold text-slate-700 transition hover:text-slate-900"
            >
              {t("nav.workflow")}
            </button>
            <Link
              to="/demo"
              className="text-sm font-semibold text-slate-700 transition hover:text-slate-900"
            >
              {t("nav.demo", "Demo")}
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-semibold text-slate-700 transition hover:text-slate-900"
            >
              {t("nav.pricing")}
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-cyan-500/20 transition hover:translate-y-[-2px]"
            >
              {t("nav.launchEffiTrack")} <FiArrowRight className="h-4 w-4" />
            </Link>
          </nav>

          <button
            className="rounded-lg p-2 text-slate-700 transition hover:bg-slate-100 md:hidden"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-slate-200 bg-white/95 px-6 py-4 shadow-md md:hidden">
            <nav className="flex flex-col gap-4 text-sm font-semibold text-slate-700">
              <button
                type="button"
                className="text-left hover:text-slate-900"
                onClick={() => {
                  handleScrollTo("platform");
                  setMobileMenuOpen(false);
                }}
              >
                {t("nav.platform")}
              </button>
              <button
                type="button"
                className="text-left hover:text-slate-900"
                onClick={() => {
                  handleScrollTo("workflow");
                  setMobileMenuOpen(false);
                }}
              >
                {t("nav.workflow")}
              </button>
              <Link
                to="/demo"
                className="hover:text-slate-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.demo", "Demo")}
              </Link>
              <Link
                to="/pricing"
                className="hover:text-slate-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.pricing")}
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-400 to-emerald-400 px-5 py-3 text-slate-900 shadow-lg shadow-cyan-500/20"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("nav.launchEffiTrack")} <FiArrowRight className="h-4 w-4" />
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        <section className="relative overflow-hidden pt-28 pb-16 lg:pb-24">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(34,211,238,0.15),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(16,185,129,0.12),transparent_32%),radial-gradient(circle_at_50%_70%,rgba(59,130,246,0.12),transparent_38%)]" />
            <div className="absolute inset-0 bg-gradient-to-b from-white via-sky-50 to-slate-50" />
          </div>

          <div className="relative mx-auto flex max-w-6xl flex-col items-start gap-12 px-6 lg:flex-row lg:items-center">
            <div className="flex-1 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-cyan-700 ring-1 ring-cyan-100 shadow-sm">
                <FiActivity className="h-4 w-4 text-cyan-600" />
                {t("hero.badge")}
              </div>
              <h1 className="display-font text-4xl leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
                {t("hero.title")}
              </h1>
              <p className="max-w-2xl text-lg leading-relaxed text-slate-700">
                {t("hero.subtitle")}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-400 to-emerald-400 px-6 py-3 text-base font-semibold text-slate-900 shadow-lg shadow-cyan-500/25 transition hover:-translate-y-0.5"
                >
                  {t("hero.getStartedFree")} <FiArrowRight className="h-5 w-5" />
                </Link>
                <button
                  type="button"
                  onClick={() => handleScrollTo("platform")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white/70 px-6 py-3 text-base font-semibold text-slate-800 shadow-sm transition hover:border-cyan-200 hover:shadow-md"
                >
                  {t("hero.seeThePlatform")}
                  <FiCheckCircle className="h-5 w-5 text-emerald-500" />
                </button>
              </div>
              <div className="grid w-full gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {proofPoints.map((point, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 shadow-sm backdrop-blur"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-50">
                      {point.icon}
                    </div>
                    <div>
                      <p className="display-font text-xl font-semibold text-slate-900">{point.value}</p>
                      <p className="text-sm text-slate-600">{point.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative flex-1">
              <div className="absolute -inset-10 -z-10 bg-gradient-to-br from-cyan-300/25 via-emerald-300/15 to-transparent blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-2xl backdrop-blur-xl">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-600">{t("dashboard.opsHealth")}</p>
                    <p className="display-font text-3xl font-semibold text-slate-900">{t("dashboard.onTrack")}</p>
                    <p className="text-sm text-emerald-600">{t("dashboard.weekOverWeek")}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">{t("dashboard.signal")}</div>
                    <div className="display-font text-2xl font-semibold text-amber-600">{t("dashboard.lowEffort")}</div>
                    <p className="text-xs text-slate-500">{t("dashboard.automationsAbsorbing")}</p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[
                    { label: t("dashboard.jobsToday"), value: "42", delta: "+12%", color: "text-cyan-600" },
                    { label: t("dashboard.invoicesSent"), value: "28", delta: t("dashboard.autoInvoice"), color: "text-emerald-600" },
                    { label: t("dashboard.avgResponse"), value: "6 min", delta: "-32%", color: "text-amber-600" },
                    { label: t("dashboard.onTimeRate"), value: "97%", delta: "+4pts", color: "text-indigo-600" },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                    >
                      <p className="text-xs uppercase tracking-wide text-slate-500">{item.label}</p>
                      <p className={`display-font text-2xl font-semibold text-slate-900 ${item.color ? item.color : ""}`}>
                        {item.value}
                      </p>
                      <p className="text-xs text-slate-500">{item.delta}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>{t("dashboard.liveWorkflow")}</span>
                    <span className="flex items-center gap-2 text-emerald-600">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      {t("dashboard.inSync")}
                    </span>
                  </div>
                  <div className="mt-4 flex flex-col gap-3">
                    {[t("dashboard.estimateAccepted"), t("dashboard.crewDispatched"), t("dashboard.invoiceSent"), t("dashboard.paymentReceived")].map((label, idx) => (
                      <div key={idx} className="flex items-center justify-between rounded-xl bg-white px-3 py-2 shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="flex h-2.5 w-2.5 rounded-full bg-gradient-to-r from-cyan-300 to-emerald-300" />
                          <span className="text-sm text-slate-800">{label}</span>
                        </div>
                        <FiArrowRight className="h-4 w-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <img
                    src={appImage}
                    alt="EffiTrack product preview"
                    className="w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="platform" className="border-t border-slate-200 bg-white py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-700">{t("platform.title")}</p>
              <h2 className="display-font text-3xl font-semibold text-slate-900 sm:text-4xl">
                {t("platform.heading")}
              </h2>
              <p className="max-w-3xl text-lg text-slate-600">
                {t("platform.description")}
              </p>
              <div className="flex flex-wrap items-center gap-4 pt-2 text-xs text-slate-600">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">{t("platform.tags.autoSms")}</span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">{t("platform.tags.crewTimelines")}</span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">{t("platform.tags.roleBasedControls")}</span>
              </div>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
              {features.map((feature, idx) => (
                <button
                  key={feature.title}
                  type="button"
                  onMouseEnter={() => setActiveFeature(idx)}
                  onFocus={() => setActiveFeature(idx)}
                  className={`group relative flex h-full flex-col items-start gap-4 rounded-3xl border border-slate-200 bg-white p-5 text-left transition hover:-translate-y-1 hover:border-cyan-200 hover:shadow-2xl hover:shadow-cyan-500/10 ${
                    activeFeature === idx ? "border-cyan-300/60 shadow-cyan-500/20" : ""
                  }`}
                >
                  <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${feature.accent} opacity-0 transition group-hover:opacity-80`} />
                  <div className="relative flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-900">
                    {feature.icon}
                  </div>
                  <div className="relative space-y-2">
                    <h3 className="display-font text-xl font-semibold text-slate-900">{feature.title}</h3>
                    <p className="text-sm text-slate-600">{feature.desc}</p>
                  </div>
                  <div className="relative flex flex-wrap gap-2 text-xs text-slate-700">
                    {feature.takeaways.map((item) => (
                      <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                        {item}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6 shadow-xl shadow-cyan-500/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-600">{t("platform.liveSpotlight")}</p>
                    <h3 className="display-font text-2xl font-semibold text-slate-900">{features[activeFeature].title}</h3>
                    <p className="text-sm text-slate-500">{t("platform.keptFresh")}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-emerald-600">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    {t("platform.synced")}
                  </div>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {features[activeFeature].takeaways.map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <span className="mt-1 flex h-2 w-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" />
                      <p className="text-sm text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">{t("platform.opsImpact")}</p>
                    <p className="display-font text-xl font-semibold text-slate-900">{t("platform.teamsShipFaster")}</p>
                  </div>
                  <Link to="/demo" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 hover:text-cyan-900">
                    {t("platform.watchDemo")} <FiArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <p className="text-sm font-semibold text-slate-600">{t("payments.title")}</p>
                <p className="display-font text-xl font-semibold text-slate-900">{t("payments.heading")}</p>
                <p className="mt-2 text-sm text-slate-600">
                  {t("payments.description")}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  {payments.map((payment) => (
                    <div
                      key={payment.name}
                      className="flex h-12 w-20 items-center justify-center rounded-xl border border-slate-200 bg-white px-2 py-1 shadow-sm"
                    >
                      <img src={payment.src} alt={payment.name} className="h-full w-full object-contain" />
                    </div>
                  ))}
                </div>
                <div className="mt-6 space-y-2 text-sm text-slate-700">
                  <div className="flex items-center gap-2">
                    <FiCheckCircle className="h-4 w-4 text-emerald-600" />
                    <span>{t("payments.autoSync")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiShield className="h-4 w-4 text-emerald-600" />
                    <span>{t("payments.bankGrade")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FiZap className="h-4 w-4 text-amber-500" />
                    <span>{t("payments.instantNotifications")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* More Features Bento Grid */}
        <section className="border-t border-slate-200 bg-gradient-to-b from-slate-50 to-white py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-700">{t("moreFeatures.title")}</p>
              <h2 className="display-font text-3xl font-semibold text-slate-900 sm:text-4xl">
                {t("moreFeatures.heading")}
              </h2>
              <p className="max-w-3xl text-lg text-slate-600">
                {t("moreFeatures.description")}
              </p>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {moreFeatureItems.map(({ key, icon, color, bg }) => (
                <div
                  key={key}
                  className="group flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:border-cyan-200 hover:shadow-lg"
                >
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${bg} ${color}`}>
                    {icon}
                  </div>
                  <div>
                    <h3 className="display-font text-base font-semibold text-slate-900">{t(`moreFeatures.items.${key}.title`)}</h3>
                    <p className="mt-1 text-sm text-slate-600">{t(`moreFeatures.items.${key}.desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="workflow" className="border-y border-slate-200 bg-slate-50 py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-cyan-700">{t("workflow.title")}</p>
              <h2 className="display-font text-3xl font-semibold text-slate-900 sm:text-4xl">
                {t("workflow.heading")}
              </h2>
              <p className="max-w-3xl text-lg text-slate-600">
                {t("workflow.description")}
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {workflow.map((step) => (
                <div
                  key={step.title}
                  className="flex h-full flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:border-cyan-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-900">
                      {step.icon}
                    </div>
                    <h3 className="display-font text-lg font-semibold text-slate-900">{step.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600">{step.copy}</p>
                  <div className="mt-auto flex items-center gap-2 text-xs text-cyan-700">
                    <span className="h-2 w-2 rounded-full bg-cyan-500" />
                    {t("workflow.updatesAuto")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile App / Employee Portal Section */}
        <section className="border-t border-slate-200 bg-white py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
              <div className="flex flex-col gap-5">
                <div className="inline-flex w-fit items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-white">
                  <FiSmartphone className="h-4 w-4" />
                  {t("mobileApp.badge")}
                </div>
                <h2 className="display-font text-3xl font-semibold text-slate-900 sm:text-4xl">
                  {t("mobileApp.heading")}
                </h2>
                <p className="text-lg text-slate-600">
                  {t("mobileApp.description")}
                </p>
                <ul className="space-y-2">
                  {Array.isArray(mobileAppFeatures) && mobileAppFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm text-slate-700">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400">
                        <FiCheckCircle className="h-3.5 w-3.5 text-white" />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <a
                    href="https://apps.apple.com/us/app/effi-track/id6755919441"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-900 px-4 py-3 text-white shadow-sm transition hover:bg-slate-800 hover:-translate-y-0.5"
                  >
                    <FaApple className="h-6 w-6" />
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("mobileApp.downloadOn")}</p>
                      <p className="text-sm font-semibold">{t("mobileApp.appStore")}</p>
                    </div>
                  </a>
                  <div className="inline-flex cursor-default items-center gap-3 rounded-xl border border-slate-200 bg-slate-700 px-4 py-3 text-white/60 shadow-sm">
                    <FaGooglePlay className="h-5 w-5" />
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">{t("mobileApp.comingSoon")}</p>
                      <p className="text-sm font-semibold">{t("mobileApp.googlePlay")}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-cyan-50 p-6 shadow-xl shadow-cyan-500/10">
                  <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                    <FiSmartphone className="h-5 w-5 text-cyan-600" />
                    {t("mobileApp.badge")}
                  </div>
                  <div className="mt-5 space-y-4">
                    {mobileAppHighlights.map(({ key, icon, desc }) => (
                      <div key={key} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
                          {icon}
                        </div>
                        <div>
                          <p className="display-font text-sm font-semibold text-slate-900">{t(`mobileApp.${key}`)}</p>
                          <p className="mt-0.5 text-xs text-slate-600">{desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-center justify-between rounded-2xl border border-slate-200 bg-gradient-to-r from-cyan-50 to-emerald-50 px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="flex h-2 w-2 rounded-full bg-emerald-500" />
                      {t("dashboard.inSync")}
                    </div>
                    <Link to="/pricing" className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-900">
                      {t("hero.getStartedFree")} <FiArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="proof" className="bg-white py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-emerald-50 p-7 shadow-xl shadow-emerald-500/10">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                  <FiStar className="h-4 w-4" />
                  {t("proof.operatorsWhoSwitched")}
                </div>
                <h3 className="display-font mt-3 text-2xl font-semibold text-slate-900">
                  {t("proof.heading")}
                </h3>
                <div className="mt-6 space-y-5">
                  {testimonials.map((testimonial) => (
                    <div
                      key={testimonial.name}
                      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="display-font text-lg font-semibold text-slate-900">{testimonial.name}</p>
                          <p className="text-xs uppercase tracking-wide text-slate-500">{testimonial.role}</p>
                        </div>
                        <div className="flex items-center gap-1 text-amber-500">
                          {[...Array(5)].map((_, idx) => (
                            <FiStar key={idx} className="h-4 w-4 fill-current" />
                          ))}
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-slate-700">"{testimonial.quote}"</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">{t("proof.reliability")}</p>
                  <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-700">{t("proof.uptime")}</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { label: t("proof.liveSupport"), value: t("proof.avgResponse"), color: "text-cyan-700" },
                    { label: t("proof.dataExports"), value: t("proof.yoursAnytime"), color: "text-emerald-700" },
                    { label: t("proof.onboarding"), value: t("proof.guidedTemplates"), color: "text-amber-700" },
                    { label: t("proof.security"), value: t("proof.bankGrade"), color: "text-indigo-700" },
                  ].map((item) => (
                    <div key={item.label} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                      <p className="text-xs uppercase tracking-wide text-slate-500">{item.label}</p>
                      <p className={`display-font text-xl font-semibold text-slate-900 ${item.color ? item.color : ""}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-cyan-50 via-emerald-50 to-white px-5 py-4">
                  <p className="text-sm font-semibold text-slate-900">{t("proof.readyToSee")}</p>
                  <p className="text-sm text-slate-600">{t("proof.getLiveWalkthrough")}</p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <Link
                      to="/demo"
                      className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-md shadow-cyan-500/20"
                    >
                      {t("proof.bookDemo")} <FiArrowRight className="h-4 w-4" />
                    </Link>
                    <a
                      href="mailto:support@effitrack.com"
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:border-cyan-200"
                    >
                      {t("proof.talkWithUs")}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="border-t border-slate-200 bg-gradient-to-r from-cyan-50 via-emerald-50 to-white py-14">
          <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-6 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-cyan-700 shadow-sm ring-1 ring-cyan-100">
              {t("cta.nextStep")}
            </div>
            <h3 className="display-font text-3xl font-semibold text-slate-900 sm:text-4xl">
              {t("cta.heading")}
            </h3>
            <p className="max-w-3xl text-lg text-slate-600">
              {t("cta.description")}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-base font-semibold text-slate-900 shadow-lg shadow-cyan-500/20 transition hover:-translate-y-0.5"
              >
                {t("cta.startFreeTrial")} <FiArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="mailto:support@effitrack.com"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-6 py-3 text-base font-semibold text-slate-800 transition hover:border-cyan-200 hover:bg-white"
              >
                {t("cta.scheduleCall")}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
